import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { FaSave, FaTimes, FaSpinner, FaTrash } from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext";

const CategoriesForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [formData, setFormData] = useState({
    name: "",
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL || "http://localhost:5000";
  const { logout } = useAuth();

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return {
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
      },
    };
  };

  useEffect(() => {
    if (isEdit) {
      axios
        .get(`${API}/categories/${id}`, getAuthHeader())
        .then((res) => {
          const category = res.data;
          if (!category || Object.keys(category).length === 0) {
            alert("Category not found or not accessible.");
            navigate("/categories");
            return;
          }

          setFormData({
            name: category.name || "",
          });
        })
        .catch((err) => {
          console.error("Fetch error:", err);
          alert("Failed to fetch category data.");
        });
    }
  }, [id]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) {
      alert("Fill all mandatory fields!");
      return;
    }

    const confirmSave = window.confirm("Do you want to save this data?");
    if (!confirmSave) return;

    setLoading(true);

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const payload = isEdit
        ? { ...formData }
        : {
            ...formData,
            tenantId: user.tenantId,
            createdByUserId: user.id,
          };

      if (isEdit) {
        await axios.put(`${API}/categories/${id}`, payload, getAuthHeader());
        alert("Category updated!");
      } else {
        await axios.post(`${API}/categories`, payload, getAuthHeader());
        alert("Category saved!");
      }

      navigate("/categories");
    } catch (error) {
      console.error("Failed to save category:", error);
      alert("Failed to save category");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const confirmDelete = window.confirm("Are you sure you want to delete this data?");
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API}/categories/${id}`, getAuthHeader());
      alert("Category deleted");
      navigate("/categories");
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete category");
    }
  };

  return (
    <div className="p-1 max-w-6xl mx-auto">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-700">
            {isEdit ? "Edit Category" : "Add Category"}
          </h2>
          <div className="flex gap-2">
            <button
              type="submit"
              form="category-form"
              disabled={loading}
              className="bg-blue-600 text-white px-2 py-1 rounded flex items-center gap-2 text-[13px] hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? <FaSpinner className="animate-spin" /> : <FaSave />}
              Save
            </button>
            <button
              onClick={() => navigate("/categories")}
              disabled={loading}
              className="bg-gray-300 text-gray-800 px-2 py-1 rounded flex items-center gap-2 text-[13px] hover:bg-gray-400 disabled:opacity-50"
            >
              <FaTimes />
              Cancel
            </button>

            {isEdit && (
              <button
                onClick={handleDelete}
                disabled={loading}
                className="bg-red-600 text-white px-2 py-1 rounded flex items-center gap-2 text-[13px] hover:bg-red-700 disabled:opacity-50"
              >
                <FaTrash />
                Delete
              </button>
            )}
          </div>
        </div>

        <form id="category-form" onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">

            {/* Name */}
            <div className="flex items-center">
              <label className="w-32 text-[13px] font-medium">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="flex-1 border rounded px-2 py-1 text-[13px]"
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoriesForm;
